import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ProspectOption } from './useProspect';
import { useSender } from './useSender';
import type { DocumentEntity } from 'services/documents/entities';
import type { RelatedLink } from 'pages/flex-data/flex-data-links/types';
import { useDocumentSettingsForm } from './useDocumentSettingsForm';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { useState, useEffect, useMemo, useRef } from 'react';
import type { DocumentSettingsFormValues, Option } from './types';
import CreateCompanyModal from 'components/editor/modals/CreateCompanyModal/CreateCompanyModal';
import CreateContactModal from 'components/editor/modals/CreateContactModal/CreateContactModal';
import { useQueryClient } from '@tanstack/react-query';
import { ProspectType } from 'services/repositories/interfaces/ProspectRepository';
import SidePanelAutoComplete from '../component/SidePanelAutoComplete';
import type { Contact } from 'services/entities/PersonEntity';
import Divider from '@mui/material/Divider';
import { QueryKeys } from 'utils/queryKeys';
import { ContentType } from 'utils/getContentType';
import type { CompanyFormData } from 'pages/editor/useCreateCompany';
import type { ContactEntity } from 'services/entities/ContactEntity';
import type { MigrationCompanyId } from 'services/entities/MigrationEntities';
import { toCompanyId } from 'services/entities/MigrationEntities';
import {
  extractLegacyIdFromCompositeId,
  isCrmCompositeId,
  mapCrmCompanyToProspectOption,
  mapCrmContactToContact,
  toCompositeId,
  toProposifyCompositeId,
} from 'services/company-and-contacts/mappers';
import { useCompanyOptions } from './useCompanyOptions';
import { useContactOptions } from './useContactOptions';
import { useGetCrmCompanyByIdQuery } from 'hooks/useGetCrmCompanyByIdQuery';
import { useGetCrmContactByIdQuery } from 'hooks/useGetCrmContactByIdQuery';
import { useDebounce } from 'hooks/useDebounce';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { FeatureFlags } from 'utils/featureFlags';
import { withMigratedLegacyIds } from './utils';
import type { CompanyResponse, ContactResponse, CreateContactRequest } from 'services/company-and-contacts/entities';

const PROSPECT_NAME_VERIFY_DELAYS_MS = [1000, 2000];

export function DocumentSettingsForm({ data, pendingCrmRelatedLinks }: { data: DocumentEntity; pendingCrmRelatedLinks?: RelatedLink[] }) {
  const { t } = useTranslation();
  const { initialValues, onValuesChange } = useDocumentSettingsForm(data);
  const queryClient = useQueryClient();
  const featureFlags = useFeatureFlag([FeatureFlags.useVariablesBff, FeatureFlags.crmDealLinking]);
  const isUseVariablesBffFlagEnabled = featureFlags[FeatureFlags.useVariablesBff];
  const isCrmDealLinkingEnabled = isUseVariablesBffFlagEnabled && featureFlags[FeatureFlags.crmDealLinking];

  const [formValues, setFormValues] = useState<DocumentSettingsFormValues>(() =>
    isCrmDealLinkingEnabled ? withMigratedLegacyIds(initialValues, data.accountId) : initialValues
  );
  const [senderOptions, setSenderOptions] = useState<Option[]>([]);
  const [isCreateCompanyModalOpen, setIsCreateCompanyModalOpen] = useState(false);
  const [isCreateContactModalOpen, setIsCreateContactModalOpen] = useState(false);
  const [contactFieldClicked, setContactFieldClicked] = useState(false);

  const { contact: contactOption, prospect } = formValues;

  const selectedContactId = isCrmDealLinkingEnabled && contactOption?.value ? String(contactOption.value) : undefined;

  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const debouncedCompanySearchTerm = useDebounce(companySearchTerm, 300);

  const {
    options: prospectOptions,
    isLoading: isProspectLoading,
    loadOptions,
  } = useCompanyOptions({
    contactId: selectedContactId,
    searchTerm: debouncedCompanySearchTerm,
  });

  const sender = useSender();
  const [localProspectOptions, setLocalProspectOptions] = useState(prospectOptions);

  const [recentlyCreatedCompanies, setRecentlyCreatedCompanies] = useState<ProspectOption[]>([]);
  useEffect(() => {
    setRecentlyCreatedCompanies((prev) =>
      // eslint-disable-next-line eqeqeq
      prev.filter((created) => !localProspectOptions?.some((option) => option.id == created.id))
    );
  }, [localProspectOptions]);

  const [verifyingProspectId, setVerifyingProspectId] = useState<string | number | null>(null);
  const verifyRunIdRef = useRef(0);
  useEffect(
    () => () => {
      verifyRunIdRef.current += 1;
    },
    []
  );

  const isCompanyNotFoundRaw =
    isCrmDealLinkingEnabled &&
    formValues.prospect !== null &&
    !!data.prospect?.id &&
    !data.prospect?.name?.trim() &&
    // eslint-disable-next-line eqeqeq
    formValues.prospect.id == data.prospect.id;
  // eslint-disable-next-line eqeqeq
  const isVerifyingCurrentProspect = verifyingProspectId !== null && verifyingProspectId == formValues.prospect?.id;
  const isCompanyNotFound = isCompanyNotFoundRaw && !isVerifyingCurrentProspect;

  const selectedCompanyId = formValues.prospect?.id ? String(formValues.prospect.id) : undefined;

  const [contactSearch, setContactSearch] = useState<string | null>(null);
  const debouncedContactSearchTerm = useDebounce(contactSearch ?? '', 300);

  const { contacts, refetch: refetchContacts } = useContactOptions({
    companyId: selectedCompanyId,
    searchTerm: debouncedContactSearchTerm,
  });

  const existingEmails = useMemo(() => {
    return contacts.map((c) => c.email?.toLowerCase().trim()).filter((email): email is string => email !== undefined);
  }, [contacts]);

  const CREATE_NEW_ACCOUNT_VALUE = 'Create a new company';
  const CREATE_NEW_CONTACT_VALUE = 'Create a new contact';

  const CREATE_ACCOUNT_OPTION = {
    label: CREATE_NEW_ACCOUNT_VALUE,
    value: CREATE_NEW_ACCOUNT_VALUE,
    id: -1,
  };

  const CREATE_CONTACT_OPTION = {
    label: CREATE_NEW_CONTACT_VALUE,
    value: CREATE_NEW_CONTACT_VALUE,
    id: -2,
  };

  const createCompanyProspect: ProspectOption = {
    ...CREATE_ACCOUNT_OPTION,
    type: ProspectType.COMPANY,
  };

  const selectedContactFallback = useMemo<Contact | null>(() => {
    if (!contactOption) {
      return null;
    }

    const numericId = Number(contactOption.value);
    const effectiveId: number | string = Number.isNaN(numericId) ? String(contactOption.value) : numericId;
    if (!effectiveId) {
      return null;
    }

    const existingContact = contacts.find((contact) => String(contact.id) === String(effectiveId));
    if (existingContact) {
      return null;
    }

    const label = contactOption.label?.trim() ?? '';
    if (!label) {
      return null;
    }

    const [firstName, ...lastNameParts] = label.split(' ');
    const lastName = lastNameParts.join(' ');

    return {
      id: effectiveId,
      uid: `${contactOption.value}-contact`,
      email: '',
      firstName: firstName ?? '',
      lastName,
      fullName: label,
      companyId: prospect?.id,
      type: 'contact',
    };
  }, [contacts, contactOption, prospect]);

  // Sync local state with prospect options
  useEffect(() => {
    setLocalProspectOptions(prospectOptions);
    setSenderOptions(sender.options);
  }, [prospectOptions]);

  // A newly created company's name lags behind on document-metadata by a few seconds while it
  // syncs through the CRM pipeline, so retry before treating it as a real "not found".
  const verifyCreatedProspectName = async (prospectId: string | number) => {
    const runId = (verifyRunIdRef.current += 1);
    setVerifyingProspectId(prospectId);
    for (const delayMs of PROSPECT_NAME_VERIFY_DELAYS_MS) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      if (verifyRunIdRef.current !== runId) return;
      await queryClient.refetchQueries({ queryKey: QueryKeys.getDocumentMetadataByContentId(ContentType.DOCUMENT, data.contentId) });
      if (verifyRunIdRef.current !== runId) return;
      const refreshed = queryClient.getQueryData<DocumentEntity>(
        QueryKeys.getDocumentMetadataByContentId(ContentType.DOCUMENT, data.contentId)
      );
      // eslint-disable-next-line eqeqeq
      if (refreshed?.prospect?.id == prospectId && refreshed.prospect?.name?.trim()) break;
    }
    if (verifyRunIdRef.current !== runId) return;
    setVerifyingProspectId((current) => (current === prospectId ? null : current));
  };

  const handleCompanyCreated = async (payload?: { data: CompanyResponse; formData: CompanyFormData }) => {
    const createdCompanyName = payload?.formData.name?.trim() ?? '';

    if (payload?.data) {
      const companyId =
        isCrmDealLinkingEnabled && data.accountId ? toProposifyCompositeId(data.accountId, 'Company', payload.data.id) : payload.data.id;
      const newProspect: ProspectOption = {
        label: payload.data.name,
        value: `${companyId}-${ProspectType.COMPANY}`,
        id: companyId,
        type: ProspectType.COMPANY,
      };
      setRecentlyCreatedCompanies((prev) => [newProspect, ...prev]);
      handleAutoCompleteChange(newProspect);
      if (isCrmDealLinkingEnabled) {
        void verifyCreatedProspectName(companyId);
      }
    }

    const { options: newOptions, matchedProspect } = await loadOptions(createdCompanyName, (optionsFromLoad, inferredMatch) => {
      setLocalProspectOptions(optionsFromLoad);
      if (inferredMatch) {
        handleAutoCompleteChange(inferredMatch);
      }
    });

    const fallbackMatch =
      !matchedProspect && createdCompanyName.length > 0
        ? newOptions.find((option) => option.label.localeCompare(createdCompanyName, undefined, { sensitivity: 'accent' }) === 0)
        : undefined;

    if (!matchedProspect && fallbackMatch) {
      handleAutoCompleteChange(fallbackMatch);
    }

    const nextProspectId = (matchedProspect ?? fallbackMatch)?.id ?? formValues.prospect?.id;

    await queryClient.invalidateQueries({ queryKey: ['prospects'] });
    await queryClient.invalidateQueries({ queryKey: QueryKeys.getCompanies });
    if (isCrmDealLinkingEnabled) {
      await queryClient.invalidateQueries({ queryKey: ['searchCrmCompanies'] });
    }
    if (nextProspectId) {
      await queryClient.invalidateQueries({ queryKey: QueryKeys.getCompany(nextProspectId as MigrationCompanyId) });
      await queryClient.invalidateQueries({ queryKey: QueryKeys.getCompanyPeople(toCompanyId(nextProspectId)) });
    }
  };

  const handleContactCreated = async (payload?: { data: ContactEntity | ContactResponse; formData: CreateContactRequest }) => {
    const createdContact = payload?.data;

    setContactSearch(null);

    if (createdContact) {
      const firstName = 'firstName' in createdContact ? createdContact.firstName : createdContact.first_name;
      const lastName = 'lastName' in createdContact ? createdContact.lastName : createdContact.last_name;
      const contactLabel = [firstName, lastName].filter(Boolean).join(' ').trim() || createdContact.email;
      const contactId =
        isCrmDealLinkingEnabled && data.accountId ? toProposifyCompositeId(data.accountId, 'Client', createdContact.id) : createdContact.id;
      const selectedContact = { value: contactId, label: contactLabel };
      const updatedValues = { ...formValues, contact: selectedContact };

      setFormValues(updatedValues);
      onValuesChange({ contact: selectedContact }, updatedValues);
    }

    refetchContacts();

    if (!isCrmDealLinkingEnabled) {
      const currentProspectId = toCompanyId(formValues.prospect?.id);
      if (!currentProspectId) return;

      await queryClient.invalidateQueries({ queryKey: QueryKeys.getCompany(currentProspectId) });
      await queryClient.invalidateQueries({ queryKey: QueryKeys.getCompanyPeople(toCompanyId(currentProspectId)) });
    }
  };

  const handleSenderChange = (senderValue: string) => {
    const selectedSender = sender.options?.find((option) => option?.value?.toString() === String(senderValue ?? ''));

    if (!selectedSender || isNaN(Number(selectedSender.value))) return;
    const assignedUser = {
      value: Number(selectedSender.value),
      label: selectedSender.label || '',
    };
    const newValues = { ...formValues, assignedUser };
    setFormValues(newValues);
    onValuesChange({ assignedUser }, newValues);
  };

  const handleContactChange = (contactId: string) => {
    if (contactId === String(CREATE_CONTACT_OPTION.id)) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      setIsCreateContactModalOpen(true);
      return;
    }

    const contact = contacts.find((p) => String(p.id) === contactId);
    if (contact) {
      const newContact = { value: contact.id, label: contact.fullName };
      const newValues = { ...formValues, contact: newContact };
      setFormValues(newValues);
      onValuesChange({ contact: newContact }, newValues);
    }
  };

  const toggleCreateCompanyModal = () => {
    setIsCreateCompanyModalOpen((prev) => !prev);
  };

  const toggleCreateContactModal = () => {
    setIsCreateContactModalOpen((prev) => !prev);
  };

  const handleFieldClear = <K extends keyof DocumentSettingsFormValues>(field: K) => {
    let newValues: DocumentSettingsFormValues;
    let changedFields: Partial<DocumentSettingsFormValues>;
    if (field === 'prospect') {
      if (isCrmDealLinkingEnabled && formValues.contact !== null) {
        newValues = { ...formValues, prospect: null };
        changedFields = { prospect: null };
      } else {
        newValues = { ...formValues, prospect: null, contact: null };
        changedFields = { prospect: null, contact: null };
      }
    } else {
      newValues = { ...formValues, [field]: null };
      changedFields = { [field]: null };
    }
    setFormValues(newValues);
    onValuesChange(changedFields, newValues);
  };

  const isContactInputDisabled = () => {
    if (isCrmDealLinkingEnabled) return false;
    return formValues.prospect === null;
  };

  const handleAutoCompleteCompanyInputChange = (searchCompany: string) => {
    setCompanySearchTerm(searchCompany);
    loadOptions(searchCompany, (newOptions) => {
      setLocalProspectOptions(newOptions);
    });
  };

  const handleAutoCompleteChange = (value: ProspectOption | null) => {
    if (!value) return;

    if (value.value === CREATE_NEW_ACCOUNT_VALUE) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      toggleCreateCompanyModal();
    } else {
      const keepContact = isCrmDealLinkingEnabled && formValues.contact !== null && selectedContactId !== undefined;
      const newValues = { ...formValues, prospect: value, contact: keepContact ? formValues.contact : null };
      setFormValues(newValues);
      onValuesChange({ prospect: value }, newValues);
    }
  };

  const handleSenderSearchInputChange = (searchSender: string | null) => {
    if (searchSender && searchSender.length > 0) {
      setSenderOptions(sender.options.filter((option) => option.label.toLowerCase().includes(searchSender.toLowerCase())));
    } else {
      setSenderOptions(sender.options);
    }
  };

  const filteredContacts = contactSearch
    ? contacts.filter((option) => option.fullName.toLowerCase().includes(contactSearch.toLowerCase()))
    : null;

  const handleAutoCompleteContactInputChange = (searchContact: string | null, reason: string) => {
    if (reason !== 'input') {
      setContactSearch(null);
      return;
    }
    setContactSearch(searchContact && searchContact.length > 0 ? searchContact : null);
  };

  const getContactFromId = (contactId: string | null): Contact | null => {
    if (!contactId) return null;

    const selected = contacts.find((contact) => String(contact.id) === contactId);
    if (selected) {
      return selected;
    }

    if (selectedContactFallback && String(selectedContactFallback.id) === contactId) {
      return selectedContactFallback;
    }

    return null;
  };

  const getContacts = () => {
    const activeContacts = filteredContacts ?? contacts;
    const contactOptions = selectedContactFallback ? [...activeContacts, selectedContactFallback] : activeContacts;

    return (
      shouldHideCreateContact
        ? contactOptions
        : [
            {
              id: CREATE_CONTACT_OPTION.id.toString(),
              fullName: CREATE_CONTACT_OPTION.label,
              type: ProspectType.PERSON,
            },
            ...contactOptions,
          ]
    ) as Contact[];
  };

  useEffect(() => {
    if (formValues.prospect) {
      setContactFieldClicked(false);
    }
  }, [formValues.prospect]);

  const relatedCompany = pendingCrmRelatedLinks?.find((o) => o.normalizedType === 'Company');
  const relatedContact = pendingCrmRelatedLinks?.find((o) => o.normalizedType === 'Contact');
  const relatedCompanyId = relatedCompany ? toCompositeId(relatedCompany.toObject) : undefined;
  const relatedContactId = relatedContact ? toCompositeId(relatedContact.toObject) : undefined;

  const { data: fetchedRelatedCompany } = useGetCrmCompanyByIdQuery(relatedCompanyId, {
    enabled: isCrmDealLinkingEnabled && !!relatedCompanyId,
  });
  const { data: fetchedRelatedContact } = useGetCrmContactByIdQuery(relatedContactId, {
    enabled: isCrmDealLinkingEnabled && !!relatedContactId,
  });

  // Latest-value refs so the auto-link effect below can read fresh state without
  // depending on it — it must run exactly once per pendingCrmRelatedLinks value,
  // not on every fetchedRelatedCompany/fetchedRelatedContact/formValues change.
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;
  const fetchedRelatedCompanyRef = useRef(fetchedRelatedCompany);
  fetchedRelatedCompanyRef.current = fetchedRelatedCompany;
  const fetchedRelatedContactRef = useRef(fetchedRelatedContact);
  fetchedRelatedContactRef.current = fetchedRelatedContact;

  const appliedCrmRelatedLinksRef = useRef<RelatedLink[] | undefined>(undefined);
  useEffect(() => {
    if (!pendingCrmRelatedLinks?.length) return;
    if (appliedCrmRelatedLinksRef.current === pendingCrmRelatedLinks) return;

    const currentFormValues = formValuesRef.current;
    if (currentFormValues.prospect && currentFormValues.contact) return;

    // The linked company/contact is fetched directly by id (see fetchedRelatedCompany/
    // fetchedRelatedContact above) rather than looked up in the dropdown's current option
    // list, since that list is now a capped/search-driven subset and may not contain it.
    if (relatedCompany && !fetchedRelatedCompanyRef.current) return;
    if (relatedContact && !fetchedRelatedContactRef.current) return;
    appliedCrmRelatedLinksRef.current = pendingCrmRelatedLinks;

    const newProspect =
      !currentFormValues.prospect && fetchedRelatedCompanyRef.current
        ? mapCrmCompanyToProspectOption(fetchedRelatedCompanyRef.current)
        : undefined;
    const newContact =
      !currentFormValues.contact && fetchedRelatedContactRef.current ? mapCrmContactToContact(fetchedRelatedContactRef.current) : undefined;

    if (newProspect || newContact) {
      const newValues = {
        ...currentFormValues,
        ...(newProspect && { prospect: newProspect }),
        ...(newContact && { contact: { value: newContact.id, label: newContact.fullName } }),
      };
      setFormValues(newValues);
      onValuesChange(
        {
          ...(newProspect && { prospect: newValues.prospect }),
          ...(newContact && { contact: newValues.contact }),
        },
        newValues
      );
    }
  }, [pendingCrmRelatedLinks, fetchedRelatedCompany, fetchedRelatedContact]);

  const contactDisabled = isContactInputDisabled();

  const shouldHideCreateCompany = isCrmDealLinkingEnabled && !!contactOption;
  const shouldHideCreateContact = isCrmDealLinkingEnabled && isCrmCompositeId(prospect?.id) && prospect?.id.split('#')[0] !== 'Proposify';

  const companyAutoCompleteOptions = useMemo(() => {
    const prospectOptions = [...recentlyCreatedCompanies, ...(localProspectOptions ?? [])];
    const base = shouldHideCreateCompany ? prospectOptions : [createCompanyProspect, ...prospectOptions];
    const selected = formValues.prospect;
    // eslint-disable-next-line eqeqeq
    if (selected && !base.some((o) => o.id == selected.id) && !isCompanyNotFound) {
      return [...base, selected];
    }
    return base;
  }, [localProspectOptions, recentlyCreatedCompanies, formValues.prospect, shouldHideCreateCompany, isCompanyNotFound]);

  return (
    <Box>
      {/*
        Company field: two implementations gated by isCrmDealLinkingEnabled.
        Once the CRM flag is fully rolled out, delete the legacy block below
        (isCrmDealLinkingEnabled === false) and keep only the CRM version.
      */}
      {isCrmDealLinkingEnabled ? (
        // CRM version — sources companies from the CRM integration, includes Proposify companies
        <SidePanelAutoComplete<ProspectOption>
          onInputChange={handleAutoCompleteCompanyInputChange}
          isLoading={isProspectLoading}
          options={companyAutoCompleteOptions}
          value={formValues.prospect ?? null}
          getOptionLabel={(option) => option.label}
          label={t('document.form.company')}
          placeholder={t('document.form.company_placeholder')}
          testId="company-select"
          textDataTestId="company-select-text-input"
          onClear={() => handleFieldClear('prospect')}
          onChange={handleAutoCompleteChange}
          noOptionsText={t('document.create_company.no_company_found')}
          error={isCompanyNotFound}
          helperText={isCompanyNotFound ? t('document.form.company_not_found') : undefined}
          renderOptions={(props, options) => {
            return (
              <React.Fragment key={`company-fragment-${options.id}`}>
                <MenuItem {...props} key={options.id} value={options.id.toString()}>
                  {options.label}
                </MenuItem>
                {options.value === CREATE_NEW_ACCOUNT_VALUE && <Divider key="divider-after-create-account" />}
              </React.Fragment>
            );
          }}
          isOptionEqualToValue={(option, value) => (option && value ? option.id == value.id : false)}
        />
      ) : null}
      {isCrmDealLinkingEnabled === false ? (
        // Legacy version — sources companies from Proposify
        <SidePanelAutoComplete<ProspectOption>
          onInputChange={handleAutoCompleteCompanyInputChange}
          isLoading={isProspectLoading}
          options={companyAutoCompleteOptions}
          value={formValues.prospect ?? null}
          getOptionLabel={(option) => option.label}
          label={t('document.form.company')}
          placeholder={t('document.form.company_placeholder')}
          testId="company-select"
          textDataTestId="company-select-text-input"
          onClear={() => handleFieldClear('prospect')}
          onChange={handleAutoCompleteChange}
          noOptionsText={t('document.create_company.no_company_found')}
          renderOptions={(props, options) => {
            return (
              <React.Fragment key={`company-fragment-${options.id}`}>
                <MenuItem {...props} key={options.id} value={options.id.toString()}>
                  {options.label}
                </MenuItem>
                {options.value === CREATE_NEW_ACCOUNT_VALUE && <Divider key="divider-after-create-account" />}
              </React.Fragment>
            );
          }}
          isOptionEqualToValue={(option, value) => (option && value ? option.id == value.id : false)}
        />
      ) : null}

      {/*
        Contact field: two implementations gated by isCrmDealLinkingEnabled.
        Once the CRM flag is fully rolled out, delete the legacy block below
        (isCrmDealLinkingEnabled === false) and keep only the CRM version.
      */}
      {isCrmDealLinkingEnabled ? (
        // CRM version — sources contacts from the CRM integration, includes Proposify contacts.
        <Box
          sx={{
            position: 'relative',
            '& .MuiFormHelperText-root': {
              transition: 'all 0.3s ease',
              height: contactFieldClicked && !formValues.prospect ? 'auto' : 0,
              opacity: contactFieldClicked && !formValues.prospect ? 1 : 0,
              margin: contactFieldClicked && !formValues.prospect ? '4px 0' : 0,
              overflow: 'hidden',
              minHeight: 0,
              display: 'block',
            },
          }}
        >
          <SidePanelAutoComplete<Contact>
            label={t('document.form.contact')}
            value={getContactFromId(formValues.contact?.value.toString() ?? null)}
            isDisabled={false}
            testId="contact-select"
            options={getContacts()}
            getOptionLabel={(option: Contact) => option.fullName}
            onClear={() => {
              handleFieldClear('contact');
              setContactSearch(null);
            }}
            renderOptions={(props, option: Contact) => (
              <React.Fragment key={`contact-${option.id}`}>
                <MenuItem {...props} key={option.id} value={option.id.toString()}>
                  {option.fullName}
                </MenuItem>
                {option.fullName === CREATE_NEW_CONTACT_VALUE && <Divider key="divider-contact" />}
              </React.Fragment>
            )}
            noOptionsText={t('No contacts found')}
            textDataTestId="contact-select-text-input"
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onInputChange={handleAutoCompleteContactInputChange}
            onChange={(option) => handleContactChange(option?.id ? String(option.id) : '')}
            placeholder={t('document.form.contact_placeholder')}
            error={contactFieldClicked && !formValues.prospect}
            helperText={
              contactFieldClicked && !formValues.prospect ? t('document.create_contact.notification.errors.no_account_selected') : ' '
            }
          />
        </Box>
      ) : null}
      {isCrmDealLinkingEnabled === false ? (
        // Legacy version — sources contacts from Proposify - disabled until a company is selected
        <Box
          sx={{
            position: 'relative',
            '& .MuiFormHelperText-root': {
              transition: 'all 0.3s ease',
              height: contactFieldClicked && !formValues.prospect ? 'auto' : 0,
              opacity: contactFieldClicked && !formValues.prospect ? 1 : 0,
              margin: contactFieldClicked && !formValues.prospect ? '4px 0' : 0,
              overflow: 'hidden',
              minHeight: 0,
              display: 'block',
            },
          }}
        >
          <SidePanelAutoComplete<Contact>
            label={t('document.form.contact')}
            value={getContactFromId(formValues.contact?.value.toString() ?? null)}
            isDisabled={contactDisabled}
            testId="contact-select"
            options={getContacts()}
            getOptionLabel={(option: Contact) => option.fullName}
            onClear={() => {
              handleFieldClear('contact');
              setContactSearch(null);
            }}
            renderOptions={(props, option: Contact) => (
              <React.Fragment key={`contact-${option.id}`}>
                <MenuItem {...props} key={option.id} value={option.id.toString()}>
                  {option.fullName}
                </MenuItem>
                {option.fullName === CREATE_NEW_CONTACT_VALUE && <Divider key="divider-contact" />}
              </React.Fragment>
            )}
            noOptionsText={t('No contacts found')}
            textDataTestId="contact-select-text-input"
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onInputChange={handleAutoCompleteContactInputChange}
            onChange={(option) => handleContactChange(option?.id ? String(option.id) : '')}
            placeholder={t('document.form.contact_placeholder')}
            error={contactFieldClicked && !formValues.prospect}
            helperText={
              contactFieldClicked && !formValues.prospect ? t('document.create_contact.notification.errors.no_account_selected') : ' '
            }
          />

          {contactDisabled && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                cursor: 'not-allowed',
                zIndex: 1,
              }}
              onClick={() => setContactFieldClicked(true)}
            />
          )}
        </Box>
      ) : null}

      <SidePanelAutoComplete<Option>
        label={t('document.form.sender')}
        testId="sender-select"
        value={formValues.assignedUser ?? null}
        onClear={() => {
          handleFieldClear('assignedUser');
          setSenderOptions(sender.options);
        }}
        options={senderOptions}
        isLoading={sender.isLoading}
        renderOptions={(props, options) => (
          <MenuItem {...props} key={options.value} value={options.value}>
            {options.label}
          </MenuItem>
        )}
        noOptionsText={t('No senders found')}
        textDataTestId="sender-select-text-input"
        isOptionEqualToValue={(option: ProspectOption | Option, value) => (option && value ? option.value == value.value : false)}
        onChange={(option) => {
          option && option.value ? handleSenderChange(String(option.value)) : handleSenderChange('');
        }}
        onInputChange={handleSenderSearchInputChange}
      />

      <CreateCompanyModal isOpen={isCreateCompanyModalOpen} onClose={toggleCreateCompanyModal} onSuccess={handleCompanyCreated} />
      {(isUseVariablesBffFlagEnabled || formValues.prospect?.id) && (
        <CreateContactModal
          isOpen={isCreateContactModalOpen}
          onClose={toggleCreateContactModal}
          onSuccess={handleContactCreated}
          companyName={formValues.prospect?.label}
          companyId={
            // CreateContactForm sources companies using the legacy Proposify companyId, so a
            // CRM composite id needs to be unwrapped to its underlying Proposify id first.
            formValues.prospect?.id && isCrmCompositeId(formValues.prospect.id)
              ? toCompanyId(extractLegacyIdFromCompositeId(formValues.prospect.id))
              : toCompanyId(formValues.prospect?.id)
          }
          existingEmails={existingEmails}
        />
      )}
    </Box>
  );
}
